import React, { useEffect, useMemo, useRef, useState } from "react";
import SpinnerField from "./SpinnerField.jsx";
import {
  areDateTimeValuesEqual,
  decrementDateTimeField,
  getDefaultDateTimeParts,
  incrementDateTimeField,
  isCompleteDateTimeValue,
  isDatePartiallyEmpty,
  normalizeDateTimeValue,
  sanitizeAmpmInput,
  sanitizeDigits,
  serializeDateTimeValue,
  toDateTimePartsFromIsoString,
} from "./spinnerUtils.js";
import { cn } from "./utils";

const EMPTY_VALUE = {
  year: "",
  month: "",
  day: "",
  hour: "",
  minute: "",
  second: "",
  ampm: "",
};

const LABELS_BY_SIZE = {
  default: {
    year: "Year",
    month: "Month",
    day: "Day",
    hour: "Hour",
    minute: "Minute",
    second: "Second",
    ampm: "AM/PM",
  },
  compact: {
    year: "YR",
    month: "MO",
    day: "DY",
    hour: "HR",
    minute: "MIN",
    second: "SEC",
    ampm: "AM/PM",
  },
};

function DateTimeSpinnerInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  className,
  size = "compact",
  shortLabels = size === "compact",
}) {
  const initialParts = toDateTimePartsFromIsoString(value) ?? EMPTY_VALUE;
  const [draft, setDraft] = useState(initialParts);
  const lastValidRef = useRef(
    toDateTimePartsFromIsoString(value) ?? getDefaultDateTimeParts(),
  );
  const rootRef = useRef(null);
  const pendingBlurValueRef = useRef(null);
  const labels = shortLabels
    ? LABELS_BY_SIZE.compact
    : LABELS_BY_SIZE.default;
  const fieldRefs = useMemo(
    () =>
      [
        React.createRef(),
        React.createRef(),
        React.createRef(),
        React.createRef(),
        React.createRef(),
        React.createRef(),
        React.createRef(),
      ],
    [],
  );

  useEffect(() => {
    const nextParts = toDateTimePartsFromIsoString(value) ?? EMPTY_VALUE;
    setDraft((currentDraft) =>
      areDateTimeValuesEqual(nextParts, currentDraft) ? currentDraft : nextParts,
    );

    if (isCompleteDateTimeValue(nextParts)) {
      lastValidRef.current = normalizeDateTimeValue(nextParts, lastValidRef.current);
    }
  }, [value]);

  const emitChange = (nextDraft) => {
    setDraft(nextDraft);

    if (isDatePartiallyEmpty(nextDraft)) {
      pendingBlurValueRef.current = null;
      onChange?.(null);
      return;
    }

    if (!isCompleteDateTimeValue(nextDraft)) {
      pendingBlurValueRef.current = null;
      return;
    }

    const normalized = normalizeDateTimeValue(nextDraft, lastValidRef.current);
    const serialized = serializeDateTimeValue(normalized);
    lastValidRef.current = normalized;
    pendingBlurValueRef.current = serialized;
    onChange?.(serialized);
  };

  const normalizeAndEmit = () => {
    if (isDatePartiallyEmpty(draft)) {
      emitChange(EMPTY_VALUE);
      return null;
    }

    const normalized = normalizeDateTimeValue(draft, lastValidRef.current);
    emitChange(normalized);
    return serializeDateTimeValue(normalized);
  };

  const focusField = (index) => {
    fieldRefs[index]?.current?.focus();
  };

  const handleFieldChange = (field, rawValue) => {
    const nextDraft =
      field === "ampm"
        ? { ...draft, ampm: sanitizeAmpmInput(rawValue) }
        : {
            ...draft,
            [field]: sanitizeDigits(rawValue, field === "year" ? 4 : 2),
          };
    emitChange(nextDraft);
  };

  const handleStep = (field, direction) => {
    const nextDraft =
      direction === "increment"
        ? incrementDateTimeField(draft, field, lastValidRef.current)
        : decrementDateTimeField(draft, field, lastValidRef.current);
    emitChange(nextDraft);
  };

  return (
    <div
      ref={rootRef}
      className={cn("grid min-w-0 items-end gap-1.5", className)}
      style={{
        gridTemplateColumns:
          "minmax(0,1.2fr) minmax(0,0.85fr) minmax(0,0.85fr) auto minmax(0,0.85fr) auto minmax(0,0.95fr) auto minmax(0,0.95fr) minmax(0,1fr)",
      }}
      onBlur={(event) => {
        if (rootRef.current?.contains(event.relatedTarget)) return;

        const nextValue =
          pendingBlurValueRef.current ??
          (isDatePartiallyEmpty(draft) ? null : normalizeAndEmit());
        pendingBlurValueRef.current = null;
        onBlur?.(nextValue);
      }}
    >
      <SpinnerField
        ref={fieldRefs[0]}
        label={labels.year}
        value={draft.year}
        size={size}
        maxLength={4}
        onChange={(nextValue) => handleFieldChange("year", nextValue)}
        onCommit={normalizeAndEmit}
        onIncrement={() => handleStep("year", "increment")}
        onDecrement={() => handleStep("year", "decrement")}
        onMoveNext={() => focusField(1)}
        onKeyDown={onKeyDown}
      />
      <SpinnerField
        ref={fieldRefs[1]}
        label={labels.month}
        value={draft.month}
        size={size}
        maxLength={2}
        onChange={(nextValue) => handleFieldChange("month", nextValue)}
        onCommit={normalizeAndEmit}
        onIncrement={() => handleStep("month", "increment")}
        onDecrement={() => handleStep("month", "decrement")}
        onMovePrevious={() => focusField(0)}
        onMoveNext={() => focusField(2)}
        onKeyDown={onKeyDown}
      />
      <SpinnerField
        ref={fieldRefs[2]}
        label={labels.day}
        value={draft.day}
        size={size}
        maxLength={2}
        onChange={(nextValue) => handleFieldChange("day", nextValue)}
        onCommit={normalizeAndEmit}
        onIncrement={() => handleStep("day", "increment")}
        onDecrement={() => handleStep("day", "decrement")}
        onMovePrevious={() => focusField(1)}
        onMoveNext={() => focusField(3)}
        onKeyDown={onKeyDown}
      />
      <div className="mx-0.5 h-7 w-px shrink-0 self-end bg-border-subtle" />
      <SpinnerField
        ref={fieldRefs[3]}
        label={labels.hour}
        value={draft.hour}
        size={size}
        maxLength={2}
        onChange={(nextValue) => handleFieldChange("hour", nextValue)}
        onCommit={normalizeAndEmit}
        onIncrement={() => handleStep("hour", "increment")}
        onDecrement={() => handleStep("hour", "decrement")}
        onMovePrevious={() => focusField(2)}
        onMoveNext={() => focusField(4)}
        onKeyDown={onKeyDown}
      />
      <span className="shrink-0 pb-2 text-sm font-semibold text-text-tertiary">:</span>
      <SpinnerField
        ref={fieldRefs[4]}
        label={labels.minute}
        value={draft.minute}
        size={size}
        maxLength={2}
        onChange={(nextValue) => handleFieldChange("minute", nextValue)}
        onCommit={normalizeAndEmit}
        onIncrement={() => handleStep("minute", "increment")}
        onDecrement={() => handleStep("minute", "decrement")}
        onMovePrevious={() => focusField(3)}
        onMoveNext={() => focusField(5)}
        onKeyDown={onKeyDown}
      />
      <span className="shrink-0 pb-2 text-sm font-semibold text-text-tertiary">:</span>
      <SpinnerField
        ref={fieldRefs[5]}
        label={labels.second}
        value={draft.second}
        size={size}
        maxLength={2}
        onChange={(nextValue) => handleFieldChange("second", nextValue)}
        onCommit={normalizeAndEmit}
        onIncrement={() => handleStep("second", "increment")}
        onDecrement={() => handleStep("second", "decrement")}
        onMovePrevious={() => focusField(4)}
        onMoveNext={() => focusField(6)}
        onKeyDown={onKeyDown}
      />
      <SpinnerField
        ref={fieldRefs[6]}
        label={labels.ampm}
        value={draft.ampm}
        size={size}
        inputMode="text"
        maxLength={2}
        onChange={(nextValue) => handleFieldChange("ampm", nextValue)}
        onCommit={normalizeAndEmit}
        onIncrement={() => handleStep("ampm", "increment")}
        onDecrement={() => handleStep("ampm", "decrement")}
        onMovePrevious={() => focusField(5)}
        onClick={() => handleStep("ampm", "increment")}
        onKeyDown={onKeyDown}
      />
    </div>
  );
}

export default DateTimeSpinnerInput;
